import type { PayStatus } from "./pay-timeline";

/**
 * The checkout's motion as plain numbers, so every renderer moves the same
 * way: the web one turns a curve into a CSS `cubic-bezier()` (`cssEase`), a
 * native one hands the same four numbers to its animation library's bezier
 * easing. The Pay sequence's beats (HOLD_MS, FOLD_DELAY_MS, FOLD_MS) are the
 * core's own and live with its timeline (pay-timeline.ts).
 */

/** A cubic Bézier easing's two control points: x1, y1, x2, y2. */
export type Curve = readonly [number, number, number, number];

/** A curve as CSS writes it. */
export const cssEase = (curve: Curve) => `cubic-bezier(${curve.join(",")})`;

/**
 * One motion, everywhere: every block that appears, leaves or changes face
 * does it with this one timing, so the checkout never has two speeds. A
 * partner cannot override it: motion is part of the checkout's manner, not
 * its palette.
 */
export const MOTION = {
  durationMs: 240,
  curve: [0.2, 0.7, 0.2, 1] as Curve,
  /** How far a block travels while fading in, in px. */
  risePx: 6,
} as const;

/** The fold: a deliberate collapse, easing in and out, not a snap. */
export const FOLD_CURVE: Curve = [0.45, 0, 0.2, 1];

/** Something already on screen moving to a new place: in and out. */
export const MOVE_CURVE: Curve = [0.65, 0, 0.35, 1];

/** The settling curve of the status, the success rows and a glide (FLIP)
 *  that names no other: fast out, long soft landing. */
export const SETTLE_CURVE: Curve = [0.32, 0.72, 0, 1];
/** How long a glide (FLIP) takes when it names no other duration. */
export const GLIDE_MS = 450;

/** The status label changing words: a slight overshoot, for life. */
export const MORPH_CURVE: Curve = [0.41, 1.03, 0.6, 1.03];
export const MORPH_MS = 600;

/** The status icon swapping in: a spring. */
export const ICON_IN_CURVE: Curve = [0.34, 1.56, 0.64, 1];
export const ICON_IN_MS = 450;

/** The Pay button's fill leaving: a touch slower than the fold, easing in
 *  and out so it neither snaps away nor lingers washed out. */
export const DISSOLVE_MS = 650;
export const DISSOLVE_CURVE: Curve = [0.4, 0, 0.2, 1];

/** The tickets destination's chip drawing in around the address as its ×
 *  goes. */
export const CHIP_MS = 350;

/** What the button says, spinner beside it, from Continue until the
 *  checkout can take a payment (the tickets held, the session, the buyer's
 *  attestation, the card fields): one button, one message, on every client. */
export const LOADING_CHECKOUT = "Loading checkout";

/** What the Pay button says at each step once pressed. Green is kept for
 *  the end: only "You're going" is done. */
export const PAY_STATUS_LABEL: Readonly<Record<PayStatus, string>> = {
  processing: "Processing payment",
  accepted: "Payment accepted",
  issuing: "Issuing your tickets",
  done: "You're going",
};

/**
 * What a checkout sells, for its words: `tickets` when a line admits to an
 * event, `items` when none does (an item with no event, such as a Lissen
 * music unlock). Only the words differ; the checkout runs the same way.
 */
export type CheckoutGoods = "tickets" | "items";

/** The words that name what is bought, for each kind of cart. */
export const CHECKOUT_WORDS: Readonly<
  Record<
    CheckoutGoods,
    {
      payStatus: Readonly<Record<PayStatus, string>>;
      /** A free order's button, and while it completes. */
      free: string;
      freeBusy: string;
      /** The address field's label. */
      emailFor: string;
      /** The settled address's label. */
      destination: string;
      /** Where the order's confirmation went, after the address. */
      sentTo: string;
      /** The contact step's code checked, while the purchase carries on. */
      verified: string;
    }
  >
> = {
  tickets: {
    payStatus: PAY_STATUS_LABEL,
    free: "Get tickets",
    freeBusy: "Getting tickets…",
    emailFor: "Email for your tickets",
    destination: "Tickets destination",
    sentTo: "Your tickets are on their way to",
    verified: "Verified · getting your tickets…",
  },
  items: {
    payStatus: {
      processing: "Processing payment",
      accepted: "Payment accepted",
      issuing: "Completing your order",
      done: "It's yours",
    },
    free: "Get it",
    freeBusy: "Getting it…",
    emailFor: "Email for your receipt",
    destination: "Receipt to",
    sentTo: "Your receipt is on its way to",
    verified: "Verified · opening your checkout…",
  },
};
